import { CardPosition, AnnotationDef } from './MiroAnnotations';

interface MiroArrowProps {
  targetRect: DOMRect;
  cardPos: CardPosition;
  cardWidth: number;
  cardHeight: number;
  position: AnnotationDef['position'];
}

/** Taille de la pointe de flèche */
const ARROW_SIZE = 6;

/**
 * Flèche SVG courbe (courbe de Bézier cubique) entre le cadre d'annotation et l'élément ciblé.
 * Rendu dans le SVG overlay de MiroAnnotations (pointer-events: none).
 */
export function MiroArrow({ targetRect, cardPos, cardWidth, cardHeight, position }: MiroArrowProps) {
  // Point d'arrivée : centre du bord de la cible le plus proche du cadre
  const end = getEndPoint(targetRect, position);

  // Point de départ : bord du cadre le plus proche de la cible
  const start = getStartPoint(cardPos, cardWidth, cardHeight, position);

  // Points de contrôle de la courbe de Bézier
  const { ctrl1, ctrl2 } = getControlPoints(start, end, position);

  // Angle de la pointe (tangente à la courbe en end)
  const angle = Math.atan2(end.y - ctrl2.y, end.x - ctrl2.x) * (180 / Math.PI);

  // Points du triangle de la pointe (centré sur end, orienté vers l'arrivée)
  const tip = buildArrowhead(end, angle);

  return (
    <g>
      {/* Courbe de Bézier */}
      <path
        d={`M ${start.x} ${start.y} C ${ctrl1.x} ${ctrl1.y}, ${ctrl2.x} ${ctrl2.y}, ${end.x} ${end.y}`}
        stroke="#f59e0b"
        strokeWidth="1.5"
        fill="none"
        opacity="0.7"
        strokeLinecap="round"
      />
      {/* Pointe de flèche */}
      <polygon
        points={tip}
        fill="#f59e0b"
        opacity="0.7"
      />
    </g>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

type Point = { x: number; y: number };

/** Centre du bord de la cible orienté vers le cadre */
function getEndPoint(rect: DOMRect, position: AnnotationDef['position']): Point {
  switch (position) {
    case 'left':  return { x: rect.left,   y: rect.top + rect.height / 2 };
    case 'right': return { x: rect.right,  y: rect.top + rect.height / 2 };
    case 'top':   return { x: rect.left + rect.width / 2, y: rect.top    };
    case 'bottom':
    default:      return { x: rect.left + rect.width / 2, y: rect.bottom };
  }
}

/** Bord du cadre orienté vers la cible */
function getStartPoint(
  cardPos: CardPosition,
  cardWidth: number,
  cardHeight: number,
  position: AnnotationDef['position']
): Point {
  switch (position) {
    case 'left':
      // Cadre à gauche → partir du bord droit du cadre
      return { x: cardPos.left + cardWidth, y: cardPos.top + cardHeight / 2 };
    case 'right':
      // Cadre à droite → partir du bord gauche du cadre
      return { x: cardPos.left, y: cardPos.top + cardHeight / 2 };
    case 'top':
      // Cadre au-dessus → partir du bas du cadre
      return { x: cardPos.left + cardWidth / 2, y: cardPos.top + cardHeight };
    case 'bottom':
    default:
      // Cadre en-dessous → partir du haut du cadre
      return { x: cardPos.left + cardWidth / 2, y: cardPos.top };
  }
}

/** Points de contrôle Bézier pour une courbe naturelle */
function getControlPoints(
  start: Point,
  end: Point,
  position: AnnotationDef['position']
): { ctrl1: Point; ctrl2: Point } {
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  switch (position) {
    case 'left':
    case 'right':
      // Courbe horizontale : les points de contrôle sont décalés horizontalement
      return {
        ctrl1: { x: start.x + dx * 0.4, y: start.y },
        ctrl2: { x: end.x - dx * 0.4,   y: end.y   },
      };
    case 'top':
    case 'bottom':
    default:
      // Courbe verticale : les points de contrôle sont décalés verticalement
      return {
        ctrl1: { x: start.x,       y: start.y + dy * 0.4 },
        ctrl2: { x: end.x,         y: end.y   - dy * 0.4 },
      };
  }
}

/** Génère le string "points" d'un triangle pointant dans la direction angle (degrés) */
function buildArrowhead(tip: Point, angleDeg: number): string {
  const rad = (angleDeg * Math.PI) / 180;
  const perpRad = rad + Math.PI / 2;

  // Pointe
  const p0 = tip;
  // Base gauche
  const p1 = {
    x: tip.x - ARROW_SIZE * Math.cos(rad) + (ARROW_SIZE * 0.5) * Math.cos(perpRad),
    y: tip.y - ARROW_SIZE * Math.sin(rad) + (ARROW_SIZE * 0.5) * Math.sin(perpRad),
  };
  // Base droite
  const p2 = {
    x: tip.x - ARROW_SIZE * Math.cos(rad) - (ARROW_SIZE * 0.5) * Math.cos(perpRad),
    y: tip.y - ARROW_SIZE * Math.sin(rad) - (ARROW_SIZE * 0.5) * Math.sin(perpRad),
  };

  return `${p0.x},${p0.y} ${p1.x},${p1.y} ${p2.x},${p2.y}`;
}
